import { NavLink } from "@/components/NavLink";
import { Mail, Phone, MapPin, Clock, Instagram } from "lucide-react";

/**
 * OFFICIAL FEEL FREE HOLIDAYS LOGO
 * Do not change logo without owner approval.
 */
import officialLogo from "@/assets/feel-free-holidays-logo.png";

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  return (
    <footer className="bg-charcoal text-white" role="contentinfo">
      <div className="container-luxury py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          {/* Company Info */}
          <div className="space-y-5">
            {/* OFFICIAL LOGO - White box container for dark background */}
            <NavLink to="/" className="inline-block bg-white p-2.5 rounded-xl" aria-label="Feel Free Holidays Home">
              <img alt="Feel Free Holidays logo" width="180" height="48" loading="lazy" className="h-11 w-auto" src={officialLogo} />
            </NavLink>
            <p className="text-sm text-white/60 leading-relaxed max-w-xs">
              Since 2023, Feel Free Holidays is a premier travel consultancy based in Adajan, Surat. Recognized as the best travel agency in Surat for personalized itineraries.
            </p>
            <div className="flex space-x-3">
              <a href="https://instagram.com/feelfree.holidays" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 hover:bg-gold flex items-center justify-center transition-all duration-300" aria-label="Follow Feel Free Holidays on Instagram">
                <Instagram className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-heading font-semibold text-base mb-5 text-gold uppercase tracking-wider">Quick Links</h3>
            <nav aria-label="Footer quick links">
              <ul className="space-y-3">
                <li><NavLink to="/" className="text-sm text-white/60 hover:text-gold transition-colors">Home</NavLink></li>
                <li><NavLink to="/about" className="text-sm text-white/60 hover:text-gold transition-colors">About Us</NavLink></li>
                <li><NavLink to="/services" className="text-sm text-white/60 hover:text-gold transition-colors">Services</NavLink></li>
                <li><NavLink to="/destinations" className="text-sm text-white/60 hover:text-gold transition-colors">Destinations</NavLink></li>
                <li><NavLink to="/blog" className="text-sm text-white/60 hover:text-gold transition-colors">Blog</NavLink></li>
                <li><NavLink to="/faq" className="text-sm text-white/60 hover:text-gold transition-colors">FAQ</NavLink></li>
              </ul>
            </nav>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-heading font-semibold text-base mb-5 text-gold uppercase tracking-wider">Our Services</h3>
            <nav aria-label="Footer services links">
              <ul className="space-y-3">
                <li><NavLink to="/services" className="text-sm text-white/60 hover:text-gold transition-colors">Customized Holidays</NavLink></li>
                <li><NavLink to="/services" className="text-sm text-white/60 hover:text-gold transition-colors">Honeymoon Packages</NavLink></li>
                <li><NavLink to="/services" className="text-sm text-white/60 hover:text-gold transition-colors">Family Tours</NavLink></li>
                <li><NavLink to="/visa" className="text-sm text-white/60 hover:text-gold transition-colors">Visa Assistance</NavLink></li>
                <li><NavLink to="/services" className="text-sm text-white/60 hover:text-gold transition-colors">Passport Application</NavLink></li>
              </ul>
            </nav>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-heading font-semibold text-base mb-5 text-gold uppercase tracking-wider">Contact Us</h3>
            <address className="not-italic">
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-gold flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <a href="https://www.google.com/maps/dir//18th+Floor,+The+Junomoneta+Tower,+1806,+opp.+Pal+RTO,+near+Rajhans+Cinema,+Pal,+Adajan,+Surat,+Gujarat+394510/@21.1835438,72.78583,17z" target="_blank" rel="noopener noreferrer" className="text-sm text-white/60 hover:text-gold transition-colors leading-relaxed">
                    18th Floor, The Junomoneta Tower,<br />
                    Opp. Pal RTO, Near Rajhans Cinema,<br />
                    Adajan, Surat, Gujarat 394510
                  </a>
                </li>
                <li className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-gold flex-shrink-0" aria-hidden="true" />
                  <div className="flex flex-col gap-1">
                    <a href="tel:+917490019959" className="text-sm text-white/60 hover:text-gold transition-colors">
                      +91 74900 19959
                    </a>
                    <a href="tel:+919924619959" className="text-sm text-white/60 hover:text-gold transition-colors">
                      +91 99246 19959
                    </a>
                  </div>
                </li>
                <li className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-gold flex-shrink-0" aria-hidden="true" />
                  <button onClick={() => window.location.href = 'mailto:' + 'info' + '@' + 'feelfreeholidays.com'} className="text-sm text-white/60 hover:text-gold transition-colors cursor-pointer bg-transparent border-none p-0" aria-label="Send email to Feel Free Holidays">
                    Click to Email Us
                  </button>
                </li>
                <li className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-gold flex-shrink-0" aria-hidden="true" />
                  <span className="text-sm text-white/60">
                    Mon – Sat: 10 AM – 7 PM
                  </span>
                </li>
              </ul>
            </address>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <p className="text-sm text-white/40 text-center mb-6 max-w-xl mx-auto">
            Proudly serving travelers in Adajan, Pal, and all of Surat for domestic and international holidays.
          </p>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/40">
              © {currentYear} Feel Free Holidays. All rights reserved.
            </p>
            <nav aria-label="Legal links" className="flex gap-6">
              <NavLink to="/privacy-policy" className="text-sm text-white/40 hover:text-gold transition-colors">
                Privacy Policy
              </NavLink>
              <NavLink to="/terms" className="text-sm text-white/40 hover:text-gold transition-colors">
                Terms & Conditions
              </NavLink>
            </nav>
          </div>
        </div>
      </div>
    </footer>
  );
};